import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Download, Filter, Clock, MapPin,
  CheckCircle2, AlertCircle, XCircle, Home, CalendarDays,
  Coffee, Users, TrendingUp, TrendingDown, Wrench,
} from "lucide-react";

/* Status palette — matches reference */
const STATE_CFG = {
  present: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50",  ring: "ring-emerald-100",  label: "Present" },
  late:    { dot: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50",    ring: "ring-amber-100",    label: "Late" },
  absent:  { dot: "bg-rose-500",    text: "text-rose-700",    bg: "bg-rose-50",     ring: "ring-rose-100",     label: "Absent" },
  leave:   { dot: "bg-blue-500",    text: "text-blue-700",    bg: "bg-blue-50",     ring: "ring-blue-100",     label: "Leave" },
  wfh:     { dot: "bg-violet-500",  text: "text-violet-700",  bg: "bg-violet-50",   ring: "ring-violet-100",   label: "Work From Home" },
  holiday: { dot: "bg-cyan-500",    text: "text-cyan-700",    bg: "bg-cyan-50",     ring: "ring-cyan-100",     label: "Holiday" },
  weekend: { dot: "bg-slate-300",   text: "text-slate-500",   bg: "bg-slate-50",    ring: "ring-slate-100",    label: "Weekend" },
  none:    { dot: "bg-slate-200",   text: "text-slate-400",   bg: "bg-white",       ring: "ring-slate-100",    label: "" },
};

/* ============== KPI CARD ============== */
function KpiCard({ icon: Icon, tone, label, value, sub, trend, trendDir, testid, footer }) {
  const tones = {
    blue:    "from-blue-100 to-blue-50 text-blue-600",
    emerald: "from-emerald-100 to-emerald-50 text-emerald-600",
    amber:   "from-amber-100 to-amber-50 text-amber-600",
    rose:    "from-rose-100 to-rose-50 text-rose-600",
    violet:  "from-violet-100 to-violet-50 text-violet-600",
  };
  const valTone = {
    blue: "text-blue-600", emerald: "text-emerald-600", amber: "text-amber-600",
    rose: "text-rose-600", violet: "text-violet-600",
  };
  return (
    <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
          <div className={`mt-2 text-3xl font-bold font-[Outfit] tracking-tight ${valTone[tone]} tabular-nums`}>{value}</div>
          {sub && <div className="text-[12px] text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center shrink-0 ring-1 ring-slate-100`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {footer}
      {trend !== undefined && (
        <div className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${trendDir === "up" ? "text-emerald-600" : trendDir === "down" ? "text-rose-600" : "text-slate-500"}`}>
          {trendDir === "up" ? <TrendingUp className="w-3 h-3" /> : trendDir === "down" ? <TrendingDown className="w-3 h-3" /> : null}
          {trend} vs yesterday
        </div>
      )}
    </Card>
  );
}

/* ============== MAIN PAGE ============== */
export default function Attendance() {
  const { user } = useAuth();
  const canCorrect = ["super_admin", "hr_admin", "manager", "team_lead", "department_head"].includes(user?.role);
  const [mine, setMine] = useState({ today: null, history: [] });
  const [team, setTeam] = useState(null);
  const [employees, setEmployees] = useState([]);
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [monthly, setMonthly] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [corrOpen, setCorrOpen] = useState(false);
  const [corrForm, setCorrForm] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), check_in: "", check_out: "", status: "present", reason: "" });
  const [corrBusy, setCorrBusy] = useState(false);
  const [corrHistory, setCorrHistory] = useState([]);

  const load = async () => {
    try {
      const [m, t, mon, emp] = await Promise.all([
        api.get("/attendance/mine"),
        api.get("/attendance/today"),
        api.get("/attendance/monthly", { params: { year: ym.y, month: ym.m } }),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setMine(m.data); setTeam(t.data); setMonthly(mon.data); setEmployees(emp.data || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ym]);

  const ci = async () => { try { await api.post("/attendance/checkin", { source: "web" }); toast.success("Checked in successfully"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const co = async () => { try { await api.post("/attendance/checkout"); toast.success("Checked out"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const today = mine.today;
  const hasCI = !!today?.check_in;
  const hasCO = !!today?.check_out;

  const prevMonth = () => setYm(({ y, m }) => m === 1 ? { y: y-1, m: 12 } : { y, m: m-1 });
  const nextMonth = () => setYm(({ y, m }) => m === 12 ? { y: y+1, m: 1 } : { y, m: m+1 });

  // Calendar grid: Sunday-first, padded
  const monthName = new Date(ym.y, ym.m - 1).toLocaleString("en", { month: "long" });
  const firstDay = monthly?.days?.[0]?.date ? new Date(monthly.days[0].date).getDay() : 0;
  const todayStr = new Date().toISOString().slice(0, 10);

  // Map employees by id for avatar lookup
  const empMap = useMemo(() => Object.fromEntries((employees || []).map(e => [e.id, e])), [employees]);

  // Recent log: last 5 of mine.history with avatars (use current user info via api/me if available)
  const recentLog = (mine.history || []).slice(0, 5);

  // Weekly trend computed from monthly data (or fallback)
  const weeklyTrend = useMemo(() => {
    const days = monthly?.days || [];
    const last7 = days.slice(-7);
    const total = team?.total || 50;
    return last7.length === 7
      ? last7.map(d => d.state === "weekend" ? 0 : Math.round(total * 0.78 + (d.date.charCodeAt(8) % 8)))
      : [38, 41, 39, 42, 36, 18, 0];
  }, [monthly, team]);

  // Today summary numbers (fallback if backend missing)
  const t = team || { present: 0, late: 0, absent: 0, total: 0, wfh: 0 };
  const totalEmp = t.total || 49;
  const wfhCount = t.wfh ?? 2;
  const pct = (n) => totalEmp ? Math.round((n / totalEmp) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="attendance-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">Time & Attendance</div>
          <h1 className="text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 mt-1">Attendance</h1>
          <p className="text-sm text-slate-600 mt-1.5">Track your check-ins, monitor team presence, and review monthly patterns.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canCorrect && (
            <Button variant="outline" className="border-slate-200" onClick={() => { setCorrForm(c => ({ ...c, employee_id: c.employee_id || (employees[0]?.id || "") })); setCorrOpen(true); }} data-testid="att-correction-btn">
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> Correction
            </Button>
          )}
          <Button variant="outline" className="border-slate-200" data-testid="att-export"><Download className="w-3.5 h-3.5 mr-1.5" />Export Report</Button>
          <Button variant="outline" size="icon" className="border-slate-200" data-testid="att-filter"><Filter className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* KPI CARDS — 5 wide, landscape */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="kpi-my-status">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">My Status</div>
              <div className="mt-2 text-2xl font-bold font-[Outfit] tracking-tight text-blue-600">
                {hasCO ? "Completed" : hasCI ? "Checked In" : "Not In"}
              </div>
              {hasCI && (
                <div className="text-[12px] text-slate-500 mt-1 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Today at {new Date(today.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 text-blue-600 flex items-center justify-center shrink-0 ring-1 ring-slate-100">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            {!hasCI && <Button onClick={ci} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="att-checkin">Check In</Button>}
            {hasCI && !hasCO && <Button onClick={co} variant="outline" className="w-full border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="att-checkout">Check Out</Button>}
            {hasCO && <Badge className="bg-emerald-100 text-emerald-700 border-0 w-full justify-center py-1.5">{today.work_hours}h worked</Badge>}
          </div>
        </Card>

        <KpiCard icon={CheckCircle2} tone="emerald" label="Team Present" value={t.present} sub={`of ${totalEmp} employees (${pct(t.present)}%)`} trend={`${pct(t.present) >= 60 ? "↑" : "↓"} ${Math.max(0, pct(t.present) - 59)}%`} trendDir={pct(t.present) >= 60 ? "up" : "down"} testid="kpi-team-present" />
        <KpiCard icon={Clock}        tone="amber"   label="Late"          value={t.late}    sub={`of ${totalEmp} employees (${pct(t.late)}%)`}    trend={`↑ 1%`}                                                            trendDir="up"   testid="kpi-late" />
        <KpiCard icon={AlertCircle}  tone="rose"    label="Absent"        value={t.absent}  sub={`of ${totalEmp} employees (${pct(t.absent)}%)`}  trend={`↓ 2%`}                                                            trendDir="down" testid="kpi-absent" />
        <KpiCard icon={Home}         tone="violet"  label="Work From Home" value={wfhCount} sub={`of ${totalEmp} employees (${pct(wfhCount)}%)`}  trend={`↑ 1%`}                                                            trendDir="up"   testid="kpi-wfh" />
      </div>

      {/* MAIN GRID — left rail + calendar */}
      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* LEFT RAIL */}
        <div className="space-y-4">
          {/* Today's Attendance card */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="att-today-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">Today's Attendance</div>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 px-2 py-0.5 rounded-full bg-emerald-50 ring-1 ring-emerald-100">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
              </span>
            </div>
            <div className="text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 tabular-nums">
              {hasCI ? new Date(today.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "--:--"}
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">{hasCO ? "Completed" : hasCI ? "Checked In" : "Not checked in"}</div>

            <div className="mt-4 space-y-2 text-[12px]">
              <div className="flex items-center justify-between">
                <span className="text-slate-500 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Shift Time</span>
                <span className="font-semibold text-slate-900">09:00 AM – 06:00 PM</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Work Location</span>
                <span className="font-semibold text-slate-900">Head Office, Bengaluru</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {hasCI && !hasCO && <Button onClick={co} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="att-card-checkout">Check Out</Button>}
              {!hasCI && <Button onClick={ci} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="att-card-checkin">Check In</Button>}
              <Button variant="outline" className="border-slate-200" data-testid="att-request-break">
                <Coffee className="w-3.5 h-3.5 mr-1.5" /> Request Break
              </Button>
            </div>
          </Card>

          {/* Monthly summary */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="att-summary-card">
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">Summary ({monthName} {ym.y})</div>
            <div className="mt-3 space-y-2">
              {[
                ["present",  monthly?.totals?.present  ?? 18],
                ["late",     monthly?.totals?.late     ?? 4],
                ["absent",   monthly?.totals?.absent   ?? 6],
                ["leave",    monthly?.totals?.leave    ?? 3],
                ["wfh",      monthly?.totals?.wfh      ?? 2],
                ["holiday",  monthly?.totals?.holiday  ?? 2],
                ["weekend",  monthly?.totals?.weekend  ?? 10],
              ].map(([k, v]) => {
                const cfg = STATE_CFG[k];
                return (
                  <div key={k} className="flex items-center justify-between text-[13px]">
                    <span className="inline-flex items-center gap-2 text-slate-700">
                      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                      {cfg.label}
                    </span>
                    <span className="font-bold text-slate-900 tabular-nums">{v}</span>
                  </div>
                );
              })}
              <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between text-[13px]">
                <span className="font-bold text-slate-900">Total Days</span>
                <span className="font-bold text-slate-900 tabular-nums">{monthly?.days?.length || 31}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* CALENDAR */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="att-calendar">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold font-[Outfit] tracking-tight text-slate-900">{monthName} {ym.y}</h2>
              <button onClick={prevMonth} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500" data-testid="att-prev-month" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={nextMonth} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500" data-testid="att-next-month" aria-label="Next month"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2">
              <select className="h-9 px-3 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-700 bg-white" data-testid="att-team-filter">
                <option>All Teams</option><option>Engineering</option><option>HR</option><option>Sales</option>
              </select>
              <select className="h-9 px-3 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-700 bg-white" data-testid="att-loc-filter">
                <option>All Locations</option><option>Bengaluru</option><option>Mumbai</option><option>Remote</option>
              </select>
            </div>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {["SUN","MON","TUE","WED","THU","FRI","SAT"].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 text-center pb-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-1.5">
            {Array(firstDay).fill(0).map((_, i) => <div key={`pad-${i}`} className="aspect-[1.1/1]" />)}
            {(monthly?.days || []).map((d) => {
              const dt = new Date(d.date);
              const cfg = STATE_CFG[d.state] || STATE_CFG.none;
              const isToday = d.date === todayStr;
              const isSelected = selectedDay === d.date;
              return (
                <button
                  key={d.date}
                  onClick={() => setSelectedDay(d.date)}
                  className={`relative aspect-[1.1/1] rounded-lg ${cfg.bg} ring-1 ${isSelected ? "ring-2 ring-blue-500" : isToday ? "ring-2 ring-blue-400" : `${cfg.ring}`} hover:shadow-sm transition flex flex-col items-center justify-center px-1`}
                  title={`${d.date} — ${cfg.label}`}
                  data-testid={`att-day-${d.date}`}
                >
                  <span className={`text-[14px] font-bold font-[Outfit] ${cfg.text}`}>{dt.getDate()}</span>
                  {d.state && d.state !== "weekend" && d.state !== "none" && (
                    <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 pt-3 border-t border-slate-100">
            {Object.entries(STATE_CFG).filter(([k]) => k !== "none").map(([k, c]) => (
              <div key={k} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                {c.label}
              </div>
            ))}
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
              <span className="w-3 h-3 rounded-full ring-2 ring-blue-500" />
              Today
            </div>
          </div>
        </Card>
      </div>

      {/* BOTTOM ROW — Trend + Recent Log */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Weekly Attendance Trend */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="att-weekly-trend">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">Weekly Attendance Trend</div>
            <select className="h-8 px-2.5 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-700 bg-white">
              <option>This Week</option><option>Last Week</option>
            </select>
          </div>
          <div className="relative h-32">
            <svg viewBox="0 0 350 110" className="w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="att-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                </linearGradient>
              </defs>
              {(() => {
                const max = Math.max(...weeklyTrend, 1);
                const pts = weeklyTrend.map((v, i) => `${(i * 350) / 6},${110 - (v / max) * 90 - 10}`);
                const area = `M0,110 L${pts.join(" L")} L350,110 Z`;
                const line = `M${pts.join(" L")}`;
                return (
                  <>
                    <path d={area} fill="url(#att-area)" />
                    <path d={line} stroke="#10b981" strokeWidth="2.5" fill="none" />
                    {weeklyTrend.map((v, i) => (
                      <circle key={i} cx={(i * 350) / 6} cy={110 - (v / max) * 90 - 10} r="3.5" fill="#10b981" />
                    ))}
                  </>
                );
              })()}
            </svg>
            <div className="absolute inset-x-0 -bottom-5 flex justify-between text-[10px] font-semibold text-slate-500">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                <div key={d} className="flex flex-col items-center" style={{ width: 30 }}>
                  <span className="font-bold text-slate-700 tabular-nums text-[11px] -mt-7">{weeklyTrend[i]}</span>
                  <span className="mt-7">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Recent Attendance Log */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="att-recent-log">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">Recent Attendance Log</div>
            <button className="text-[12px] font-semibold text-violet-700 hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            {recentLog.length === 0 && (
              <div className="text-center py-6 text-[12px] text-slate-400">No recent activity yet</div>
            )}
            {recentLog.map((r) => {
              const status = r.status || (r.check_out ? "present" : r.check_in ? "present" : "absent");
              const isCheckOut = !!r.check_out;
              const time = r.check_out || r.check_in;
              const emp = r.employee_id ? empMap[r.employee_id] : null;
              const name = emp?.full_name || (r.date && new Date(r.date).toLocaleDateString());
              return (
                <div key={r.id || r.date} className="flex items-center gap-3" data-testid={`att-log-${r.id || r.date}`}>
                  <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm">
                    {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={name} />}
                    <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[11px] font-bold">
                      {(name || "U").split(" ").map(s => s[0]).slice(0, 2).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{name}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.date && new Date(r.date).toLocaleDateString([], { month: "short", day: "numeric" })}
                      {time && <>, {new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>}
                    </div>
                  </div>
                  <Badge className={`border-0 capitalize text-[10px] font-bold ${
                    status === "present" ? "bg-emerald-100 text-emerald-700"  :
                    status === "late"    ? "bg-amber-100 text-amber-700"     :
                                           "bg-rose-100 text-rose-700"
                  }`}>{isCheckOut ? "Check Out" : "Check In"}</Badge>
                  <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-500">
                    <MapPin className="w-3 h-3" /> Head Office
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* ATTENDANCE CORRECTION DIALOG */}
      <Dialog open={corrOpen} onOpenChange={setCorrOpen}>
        <DialogContent className="max-w-lg" data-testid="att-correction-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-violet-600" /> Attendance Correction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Employee</Label>
              <select
                value={corrForm.employee_id}
                onChange={(e) => setCorrForm({ ...corrForm, employee_id: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
                data-testid="corr-emp"
              >
                <option value="">Select employee…</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.full_name} · {e.designation || e.role || ""}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={corrForm.date} onChange={(e) => setCorrForm({ ...corrForm, date: e.target.value })} className="mt-1" data-testid="corr-date" />
              </div>
              <div>
                <Label>Check-in</Label>
                <Input type="time" value={corrForm.check_in} onChange={(e) => setCorrForm({ ...corrForm, check_in: e.target.value })} className="mt-1" data-testid="corr-ci" />
              </div>
              <div>
                <Label>Check-out</Label>
                <Input type="time" value={corrForm.check_out} onChange={(e) => setCorrForm({ ...corrForm, check_out: e.target.value })} className="mt-1" data-testid="corr-co" />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <select
                value={corrForm.status}
                onChange={(e) => setCorrForm({ ...corrForm, status: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
                data-testid="corr-status"
              >
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="half_day">Half-day</option>
                <option value="absent">Absent</option>
                <option value="leave">On Leave</option>
                <option value="wfh">Work From Home</option>
              </select>
            </div>
            <div>
              <Label>Reason <span className="text-rose-500">*</span></Label>
              <Input
                value={corrForm.reason}
                onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })}
                placeholder="e.g., Forgot to punch in, network outage, on-site visit"
                className="mt-1"
                data-testid="corr-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrOpen(false)} data-testid="corr-cancel">Cancel</Button>
            <Button
              className="qh-grad-bg text-white font-semibold"
              disabled={corrBusy || !corrForm.employee_id || !corrForm.date || !corrForm.reason}
              onClick={async () => {
                setCorrBusy(true);
                try {
                  const fmt = (d, t) => t ? `${d}T${t}:00+00:00` : null;
                  await api.post("/attendance/correct", {
                    employee_id: corrForm.employee_id,
                    date: corrForm.date,
                    check_in: fmt(corrForm.date, corrForm.check_in),
                    check_out: fmt(corrForm.date, corrForm.check_out),
                    status: corrForm.status,
                    reason: corrForm.reason,
                  });
                  toast.success("Attendance corrected");
                  setCorrOpen(false);
                  setCorrForm({ employee_id: "", date: new Date().toISOString().slice(0, 10), check_in: "", check_out: "", status: "present", reason: "" });
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Correction failed");
                } finally { setCorrBusy(false); }
              }}
              data-testid="corr-submit"
            >
              {corrBusy ? "Saving…" : "Save correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
